import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EntityTypeFeaturedIcon } from "@/features/entity-types/components/entity-type-featured-icon";
import { placeFormColumn } from "@/features/entity-types/lib/place-form-column";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import { Copy01, Edit05, Expand03, Trash02 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Label } from "@/components/base/label";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	TableCellContent,
	TableCellContentType,
} from "@/components/Molecules/Table/table-cell-content";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { getActionsColumnHeader } from "@/components/Molecules/Table/table-column-headers";
import { CreateJobTypeModal } from "@/components/Organisms/CreateJobTypeModal/CreateJobTypeModal";
import { DeleteJobTypeModal } from "@/components/Organisms/DeleteJobTypeModal/DeleteJobTypeModal";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useCanCreateFormLayout } from "@/features/form-editor/hooks/use-can-create-form-layout";
import { useTableSettings } from "@/hooks/use-table-settings";
import { authQueryOptions } from "@/services/auth/auth.options";
import {
	useDuplicateJobType,
	useExportJobTypes,
} from "@/services/job/job.mutations";
import { jobKeys, jobQueryOptions } from "@/services/job/job.options";
import type { JobType, JobTypesListParams } from "@/services/job/job.types";
import { organizationQueryOptions } from "@/services/organization/organization.options";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import {
	getJobTypeTableAccessDisplay,
	getJobTypeTableAccessTooltip,
} from "@/utils/job-type-detail/job-type-detail.utils";
import { resolveJobTypeDisplayIcon } from "@/utils/jobs";
import { showApiErrorToast } from "@/utils/toast.utils";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/jobTypes",
)({
	component: RouteComponent,
});

function JobTypeDeleteAction({
	jt,
	onDelete,
}: {
	jt: JobType;
	onDelete: () => void;
}) {
	const { t } = useAppTranslation();
	const canDelete = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteJobType",
			entityType: "JobType",
			entityId: jt.id,
		}),
	});

	if (!canDelete.data) return null;

	return (
		<>
			<Dropdown.Separator />
			<Dropdown.Item
				id="delete"
				label={t("admin.jobTypes.table.actions.delete")}
				icon={Trash02}
				className="[&_span]:text-red-600"
				onAction={onDelete}
			/>
		</>
	);
}

function tableKeyForOrg(organizationId: string) {
	return `orgAdmin.configuration.jobTypes.${organizationId}`;
}

const DEFAULT_COLUMN_ORDER = [
	"name",
	"description",
	"lifecycle",
	"access",
	"source",
	"form",
	"id",
	"actions",
];
const DEFAULT_SORTING = [{ id: "name", desc: false }];
const DEFAULT_PAGE_SIZE = 10;
const ACTIONS_COLUMN_ID = "actions";

const normalizeColumnOrder = (columnOrder?: string[]) => {
	const nonActionDefaultColumns = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => columnId !== ACTIONS_COLUMN_ID,
	);

	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...nonActionDefaultColumns, ACTIONS_COLUMN_ID];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter(
		(columnId) =>
			allowedColumns.has(columnId) && columnId !== ACTIONS_COLUMN_ID,
	);
	const unique = placeFormColumn(Array.from(new Set(sanitized)));
	const missingDefaults = nonActionDefaultColumns.filter(
		(columnId) => columnId !== "form" && !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults, ACTIONS_COLUMN_ID];
};

const serverSortFieldMapping: Record<string, string[]> = {
	name: ["Name"],
	description: ["Description"],
	id: ["Id"],
};

function JobTypesTable({
	organizationId,
	tableOptionsPortalTarget = null,
}: {
	organizationId: string;
	tableOptionsPortalTarget?: HTMLElement | null;
}) {
	const canEditForm = useCanCreateFormLayout();
	const viewAllOrgsQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ViewAllOrganizations",
			entityType: null,
			entityId: null,
		}),
	);
	const isPlatformAdmin = viewAllOrgsQuery.data ?? false;
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["Name"] as string[],
		"OrderBy.Descending": false,
	});
	const [selectedJobType, setSelectedJobType] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const hasAppliedPersistedSettings = useRef(false);

	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		savePageSize,
	} = useTableSettings<Record<string, never>>({
		tableKey: tableKeyForOrg(organizationId),
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: {},
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;
		if (tableSettings.searchTerm) setSearchTerm(tableSettings.searchTerm);
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [settingsLoaded, tableSettings.searchTerm, tableSettings.pageSize]);

	const listParams = useMemo<JobTypesListParams>(
		() => ({ OrganizationId: organizationId }),
		[organizationId],
	);

	const searchTrimmed = searchTerm.trim();

	const mergedServerParams = useMemo(
		() => ({
			...listParams,
			...sortParams,
			...(searchTrimmed ? { SearchTerm: searchTrimmed } : {}),
		}),
		[listParams, sortParams, searchTrimmed],
	);

	const subOrgCountQuery = useQuery({
		...organizationQueryOptions.subOrganizations.count(organizationId, {}),
		enabled: !!organizationId,
	});

	const apiStart = pageIndex * pageSize + 1;

	const countQuery = useQuery({
		...jobQueryOptions.typesCount(mergedServerParams),
	});

	const unfilteredCountQuery = useQuery({
		...jobQueryOptions.typesCount(listParams),
	});

	const pageQuery = useQuery({
		...jobQueryOptions.typesPage(apiStart, pageSize, mergedServerParams),
	});

	const data = useMemo(() => pageQuery.data ?? [], [pageQuery.data]);

	const pageCount = useMemo(() => {
		const total = countQuery.data ?? 0;
		if (!total) return 0;
		return Math.ceil(total / pageSize);
	}, [countQuery.data, pageSize]);

	const exportMutation = useExportJobTypes();

	const onSortChange = useCallback(
		({ fields, descending }: { fields: string[]; descending: boolean }) => {
			setSortParams({
				"OrderBy.Fields": fields,
				"OrderBy.Descending": descending,
			});
		},
		[],
	);

	const navigateFullDetail = useCallback(
		(jobTypeId: string) => {
			void navigate({
				to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/details",
				params: { orgId: organizationId, jobTypeId },
				search: { mode: "view", presentation: "full" },
			});
		},
		[navigate, organizationId],
	);

	const navigateFullPage = useCallback(
		(jobTypeId: string) => {
			void navigate({
				to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full",
				params: { orgId: organizationId, jobTypeId },
				search: { tab: "details", mode: "view", presentation: "full" },
			});
		},
		[navigate, organizationId],
	);

	const navigateFormEditor = useCallback(
		(jobTypeId: string) => {
			void navigate({
				to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full/form",
				params: { orgId: organizationId, jobTypeId },
				search: {
					tab: "details",
					mode: "view",
					presentation: "drawer",
				},
			});
		},
		[navigate, organizationId],
	);

	const duplicateJobTypeMutation = useDuplicateJobType();
	const tableAnchorRef = useRef<HTMLDivElement>(null);
	const duplicateJobType = useCallback(
		(jobTypeId: string) => {
			duplicateJobTypeMutation.mutate(
				{ jobTypeId, organizationId },
				{
					onSuccess: (result) => {
						if (result.affectedId) {
							navigateFullDetail(result.affectedId);
						}
					},
					onError: (error) => {
						showApiErrorToast({
							error,
							message: t("admin.jobTypes.modal.createError"),
						});
					},
				},
			);
		},
		[duplicateJobTypeMutation, navigateFullDetail, organizationId, t],
	);

	const handleRowClick = useCallback(
		(row: JobType) => {
			navigateFullDetail(row.id);
		},
		[navigateFullDetail],
	);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await queryClient.invalidateQueries({
				queryKey: [...jobKeys.jobs(), "job-types"],
			});
		} finally {
			setIsRefreshing(false);
		}
	};

	const columnLabels = useMemo(
		() => ({
			name: t("admin.jobTypes.table.columns.name"),
			description: t("admin.jobTypes.table.columns.description"),
			lifecycle: t("admin.jobTypes.table.columns.lifecycle"),
			access: t("admin.jobTypes.table.columns.access"),
			source: t("admin.jobTypes.table.columns.source"),
			form: t("admin.jobTypes.table.columns.form"),
			id: t("admin.jobTypes.table.columns.id"),
		}),
		[t],
	);

	const totalSubOrganizations = subOrgCountQuery.data ?? 0;

	const getAccessDisplay = useCallback(
		(row: JobType) =>
			getJobTypeTableAccessDisplay(row, totalSubOrganizations, t),
		[totalSubOrganizations, t],
	);

	const handleExport = useCallback(
		(visibleColumns?: string[]) => {
			const totalCount = countQuery.data ?? 0;
			if (totalCount === 0 || totalCount > 10000) return;

			const allIds = DEFAULT_COLUMN_ORDER;
			const ids = (visibleColumns?.length ? visibleColumns : allIds).filter(
				(c) => c !== "actions",
			);

			const apiColumnById: Record<string, string> = {
				name: "Name",
				description: "Description",
				lifecycle: "LifecycleId",
				source: "Source",
				id: "Id",
			};
			const apiColumns = ids
				.filter((c) => c !== "access")
				.map((c) => apiColumnById[c])
				.filter(Boolean)
				.join(",");

			exportMutation.mutate(
				{
					startIndex: 1,
					count: totalCount,
					params: {
						...mergedServerParams,
						...(apiColumns ? { columns: apiColumns } : {}),
					},
				},
				{
					onSuccess: (blob) => {
						downloadBlob(blob, generateExportFilename("job-types"));
					},
				},
			);
		},
		[countQuery.data, exportMutation, mergedServerParams],
	);

	const columns = useMemo<ColumnDef<JobType>[]>(() => {
		return [
			{
				id: "name",
				accessorKey: "name",
				header: columnLabels.name,
				enableSorting: true,
				meta: {
					headerProps: { isRowHeader: true },
				},
				minSize: 280,
				cell: ({ row }) => {
					const jt = row.original;
					const Icon = resolveJobTypeDisplayIcon(jt.icon, jt.name);
					return (
						<div className="flex items-center gap-3">
							<EntityTypeFeaturedIcon icon={Icon} />
							<div className="min-w-0">
								{jt.name ? (
									<p className="text-sm font-medium text-primary">{jt.name}</p>
								) : (
									<EmptyValue />
								)}
							</div>
						</div>
					);
				},
			},
			{
				id: "description",
				accessorKey: "description",
				header: columnLabels.description,
				enableSorting: true,
				minSize: 220,
				cell: ({ row }) => {
					return row.original.description ? (
						<span className="text-sm font-medium text-primary">
							{row.original.description}
						</span>
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "lifecycle",
				accessorFn: (row) =>
					row.statusLifecycleName?.trim() || row.lifecycleName?.trim() || "",
				header: columnLabels.lifecycle,
				enableSorting: false,
				minSize: 180,
				cell: ({ row }) => {
					const jt = row.original;
					const label =
						jt.statusLifecycleName?.trim() || jt.lifecycleName?.trim() || "";
					return label ? (
						<span className="text-sm font-medium text-primary">{label}</span>
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "access",
				accessorFn: (row) => getAccessDisplay(row).label,
				header: columnLabels.access,
				enableSorting: false,
				minSize: 200,
				meta:
					totalSubOrganizations > 0
						? {
								accessTooltip: (row: unknown) =>
									getJobTypeTableAccessTooltip(row as JobType, t),
							}
						: undefined,
				cell: ({ row }) => {
					const { label, color } = getAccessDisplay(row.original);
					return (
						<BadgeWithDot type="modern" size="md" color={color}>
							{label}
						</BadgeWithDot>
					);
				},
			},
			{
				id: "source",
				accessorFn: (row) => (row.defaultType ? "system" : "custom"),
				header: columnLabels.source,
				enableSorting: false,
				minSize: 120,
				cell: ({ row }) => (
					<Label size="md" type="dark" showIcon={false}>
						{row.original.defaultType
							? t("admin.jobTypes.table.source.system")
							: t("admin.jobTypes.table.source.custom")}
					</Label>
				),
			},
			{
				id: "form",
				header: columnLabels.form,
				enableSorting: false,
				minSize: 120,
				cell: ({ row }) => (
					<TableCellContent
						type={TableCellContentType.Secondary}
						primary={
							row.original.hasForm === true
								? t("common.actions.yes")
								: t("common.actions.no")
						}
					/>
				),
			},
			{
				id: "id",
				accessorKey: "id",
				header: columnLabels.id,
				enableSorting: true,
				minSize: 200,
				cell: ({ row }) => {
					return row.original.id ? (
						<CopyableId value={row.original.id} />
					) : (
						<EmptyValue />
					);
				},
			},
			{
				id: "actions",
				header: getActionsColumnHeader(t),
				enableSorting: false,
				enableHiding: false,
				size: 56,
				meta: {
					disableColumnReorder: true,
					rowFullViewAction: (row: unknown) =>
						navigateFullPage((row as JobType).id),
					cellProps: {
						className: "w-14 px-2",
						"data-suppress-row-click": true,
					},
				},
				cell: ({ row }) => {
					const jt = row.original;
					const editable = canEditForm;
					return (
						<div className="flex justify-end" data-suppress-row-click>
							<Dropdown.Root>
								<Dropdown.DotsButton
									data-testid="jobtype-table-row-actions-btn"
									aria-label={t("admin.jobTypes.table.actionsMenu.ariaLabel")}
								/>
								<Dropdown.Popover className="w-min">
									<Dropdown.Menu>
										<Dropdown.Item
											id="view"
											label={t("admin.jobTypes.table.actions.viewFullDetails")}
											icon={Expand03}
											onAction={() => navigateFullPage(jt.id)}
										/>
										{editable ? (
											<Dropdown.Item
												id="edit"
												label={t("admin.jobTypes.table.actions.editForm")}
												icon={Edit05}
												onAction={() => navigateFormEditor(jt.id)}
											/>
										) : null}
										<Dropdown.Item
											id="duplicate"
											data-testid="jobtype-table-action-duplicate"
											label={t("admin.jobTypes.table.actions.duplicate")}
											icon={Copy01}
											isDisabled={
												duplicateJobTypeMutation.isPending &&
												duplicateJobTypeMutation.variables?.jobTypeId === jt.id
											}
											onAction={() => duplicateJobType(jt.id)}
										/>
										<JobTypeDeleteAction
											jt={jt}
											onDelete={() =>
												setSelectedJobType({
													id: jt.id,
													name: jt.name ?? jt.id,
												})
											}
										/>
									</Dropdown.Menu>
								</Dropdown.Popover>
							</Dropdown.Root>
						</div>
					);
				},
			},
		];
	}, [
		canEditForm,
		columnLabels,
		duplicateJobType,
		duplicateJobTypeMutation.isPending,
		duplicateJobTypeMutation.variables?.jobTypeId,
		getAccessDisplay,
		navigateFormEditor,
		navigateFullPage,
		t,
		totalSubOrganizations,
	]);

	useEffect(() => {
		const total = countQuery.data ?? 0;
		if (!total) return;
		const pages = Math.max(1, Math.ceil(total / pageSize));
		if (pageIndex > 0 && pageIndex >= pages) {
			setPageIndex(Math.max(0, pages - 1));
		}
	}, [countQuery.data, pageSize, pageIndex]);

	const loadError = pageQuery.error ?? countQuery.error;

	if (loadError) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<h3 className="text-sm font-medium text-error">
					{t("admin.jobTypes.errors.failedToLoadJobTypes")}
				</h3>
				<p className="mt-1 text-sm text-tertiary">{loadError.message}</p>
				<Button
					size="sm"
					color="primary"
					onClick={handleRefresh}
					className="mt-3"
				>
					{t("common.actions.tryAgain")}
				</Button>
			</div>
		);
	}

	return (
		<>
			<div ref={tableAnchorRef} className="w-full">
				<CustomTableWrapper<JobType>
					data={data}
					columns={columns}
					showHeaderPadding={false}
					zebraStriping
					isLoading={pageQuery.isLoading || countQuery.isLoading}
					isFetching={
						pageQuery.isFetching ||
						countQuery.isFetching ||
						unfilteredCountQuery.isFetching
					}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={({ pageSize: ps, pageIndex: pi }) => {
						if (ps !== pageSize) {
							setPageSize(ps);
							setPageIndex(0);
							savePageSize(ps);
						} else if (pi !== pageIndex) {
							setPageIndex(pi);
						}
					}}
					pageCount={pageCount}
					rowCount={countQuery.data}
					totalRowCount={unfilteredCountQuery.data}
					sortFieldMapping={serverSortFieldMapping}
					defaultSortField="name"
					onSortChange={onSortChange}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					filterPlaceholder={t("admin.jobTypes.table.search.placeholder")}
					filterInputTestId="jobtype-table-search-input"
					filterValue={searchTerm}
					onDebouncedFilterChange={(s) => {
						setSearchTerm(s);
						setPageIndex(0);
						saveSearchTerm(s);
					}}
					enableTableActions
					filterLayout="bar"
					filterBarType="landing"
					filterBarClassName="pt-0"
					tableOptionsPortalTarget={tableOptionsPortalTarget}
					hasActiveFilters={searchTrimmed.length > 0}
					enableColumnReordering
					enableColumnResizing
					onRowClick={handleRowClick}
					getRowId={(row) => row.id}
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={tableSettings.columnVisibility}
					settingsLoaded={settingsLoaded}
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
					initialSorting={DEFAULT_SORTING}
				/>
			</div>

			{!pageQuery.isLoading &&
			!pageQuery.isFetching &&
			!countQuery.isFetching &&
			(countQuery.data ?? 0) === 0 &&
			searchTrimmed.length === 0 ? (
				<div className="flex justify-center py-12 text-gray-500">
					{t("admin.jobTypes.table.empty.noJobTypes")}
				</div>
			) : null}

			{!pageQuery.isLoading &&
			!pageQuery.isFetching &&
			!countQuery.isFetching &&
			(countQuery.data ?? 0) === 0 &&
			searchTrimmed.length > 0 ? (
				<TableSearchEmptyState titleKey="admin.jobTypes.table.empty.noSearchResults" />
			) : null}

			<DeleteJobTypeModal
				isOpen={selectedJobType !== null}
				setIsOpen={(open) => {
					if (!open) setSelectedJobType(null);
				}}
				handleClose={() => setSelectedJobType(null)}
				onDeleteSuccess={() => setSelectedJobType(null)}
				jobTypeId={selectedJobType?.id ?? ""}
				jobTypeName={selectedJobType?.name ?? ""}
				organizationId={isPlatformAdmin ? organizationId : undefined}
				scope="organization"
			/>
			<DuplicatingEntityTypeModal
				isOpen={duplicateJobTypeMutation.isPending}
				anchorRef={tableAnchorRef}
				label={t("admin.jobTypes.duplicate.inProgress")}
				testId="jobtype-duplicate-in-progress"
			/>
		</>
	);
}

function RouteComponent() {
	const { t } = useAppTranslation();
	const { orgId } = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/jobTypes",
	});
	const [isOpen, setIsOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isFullRoute = /\/configuration\/jobTypes\/[^/]+\/full/.test(pathname);

	const canCreateJobType = useQuery(
		authQueryOptions.checkAction({
			name: "CreateJobType",
		}),
	);

	if (isFullRoute) {
		return <Outlet />;
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("admin.jobTypes.table.title")}
						description={t("admin.jobTypes.table.orgDescription")}
						actions={
							canCreateJobType.data
								? [
										{
											label: t("admin.jobTypes.table.actions.createJobType"),
											color: "accent",
											onPress: () => setIsOpen(true),
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<JobTypesTable
					key={orgId}
					organizationId={orgId}
					tableOptionsPortalTarget={tableOptionsHost}
				/>
			</PageLayout>

			{canCreateJobType.data ? (
				<CreateJobTypeModal
					isOpen={isOpen}
					setIsOpen={setIsOpen}
					handleClose={() => {
						setIsOpen(false);
					}}
					mode="organization"
					organizationId={orgId}
				/>
			) : null}
			<Outlet />
		</>
	);
}
